import { useEffect, useMemo, useState } from 'react';
import { useWizardStore } from '../../stores/wizardStore';
import { useWizardOptions } from '../../hooks/useWizardOptions';
import { fetchPlaybooks } from '../../lib/api';
import type { PlaybookSummary } from '../../types/wizard';

export default function ProjectContextStep() {
  const { projectContext, updateProjectContext, selectedPlaybook, setSelectedPlaybook } = useWizardStore();
  const { options, loading } = useWizardOptions();
  const [playbookInfo, setPlaybookInfo] = useState<PlaybookSummary | null>(null);

  // Memoize to keep stable references — avoids infinite useEffect loops
  // when options is null during loading (the ?? [] fallback would create a
  // new array every render, retriggering the goal-sync effect).
  const projectGoals = useMemo(() => options?.projectContext.projectGoals ?? [], [options]);
  const industries = options?.projectContext.industries ?? [];
  const complianceOptions = options?.projectContext.complianceOptions ?? [];

  // When goal changes, auto-select the mapped playbook
  useEffect(() => {
    if (!projectContext.goal || projectGoals.length === 0) {
      setSelectedPlaybook(null);
      setPlaybookInfo(null);
      return;
    }
    const match = projectGoals.find((g) => g.label === projectContext.goal);
    if (match) {
      setSelectedPlaybook(match.playbookId);
    }
  }, [projectContext.goal, projectGoals, setSelectedPlaybook]);

  // Fetch playbook summary when selectedPlaybook changes
  useEffect(() => {
    if (!selectedPlaybook) {
      setPlaybookInfo(null);
      return;
    }
    let cancelled = false;
    fetchPlaybooks().then((playbooks) => {
      if (cancelled) return;
      const found = playbooks.find((p) => p.id === selectedPlaybook);
      setPlaybookInfo(found ?? null);
    }).catch(() => {
      if (!cancelled) setPlaybookInfo(null);
    });
    return () => { cancelled = true; };
  }, [selectedPlaybook]);

  if (loading) return <div className="text-gray-500 text-sm py-8 text-center">Loading configuration...</div>;

  const toggleCompliance = (value: string) => {
    const current = projectContext.compliance;
    if (current.includes(value)) {
      updateProjectContext({
        compliance: current.filter((c) => c !== value),
      });
    } else {
      updateProjectContext({ compliance: [...current, value] });
    }
  };

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-xl font-semibold text-white mb-1">
          Project Context
        </h2>
        <p className="text-sm text-gray-500">
          Tell us about your project so we can tailor the AI blueprint.
        </p>
      </div>

      {/* Project Name */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          Project Name <span className="text-red-400">*</span>
        </label>
        <input
          type="text"
          value={projectContext.name}
          onChange={(e) => updateProjectContext({ name: e.target.value })}
          placeholder="e.g., acme-platform"
          className="input-field"
        />
      </div>

      {/* Project Goal */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          Project Goal
        </label>
        <select
          value={projectContext.goal}
          onChange={(e) => updateProjectContext({ goal: e.target.value })}
          className="select-field"
        >
          <option value="">Select a project goal...</option>
          {projectGoals.map((goal) => (
            <option key={goal.label} value={goal.label}>
              {goal.label}
            </option>
          ))}
        </select>

        {/* Playbook indicator */}
        {playbookInfo && (
          <div className="mt-2 flex items-center gap-2 text-sm">
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-accent/10 border border-accent/20 text-accent-300">
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
              {playbookInfo.name}
            </span>
            <span className="text-gray-500">
              {playbookInfo.phaseCount} phases
            </span>
            <a
              href={`/playbooks/${playbookInfo.id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-accent-400 hover:text-accent-300 underline underline-offset-2"
            >
              View phases
            </a>
          </div>
        )}
      </div>

      {/* Project Description */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          Project Description
        </label>
        <textarea
          value={projectContext.description}
          onChange={(e) =>
            updateProjectContext({ description: e.target.value })
          }
          placeholder="Describe your project in a few sentences. This helps generate more accurate configurations..."
          rows={4}
          className="input-field resize-none"
        />
        <p className="mt-1.5 text-xs text-gray-600">
          {projectContext.description.length}/500 characters
        </p>
      </div>

      {/* Industry */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          Industry / Domain
        </label>
        <select
          value={projectContext.industry}
          onChange={(e) => updateProjectContext({ industry: e.target.value })}
          className="select-field"
        >
          <option value="">Select an industry...</option>
          {industries.map((ind) => (
            <option key={ind} value={ind}>
              {ind}
            </option>
          ))}
        </select>
      </div>

      {/* Compliance Requirements */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-3">
          Compliance Requirements
        </label>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
          {complianceOptions.map((option) => (
            <label
              key={option}
              className={`
                flex items-center gap-2 px-3 py-2.5 rounded-lg border cursor-pointer transition-all duration-200 text-sm
                ${
                  projectContext.compliance.includes(option)
                    ? 'bg-accent/10 border-accent/30 text-accent-300'
                    : 'bg-surface-DEFAULT border-dark-border text-gray-400 hover:border-surface-300'
                }
              `}
            >
              <input
                type="checkbox"
                checked={projectContext.compliance.includes(option)}
                onChange={() => toggleCompliance(option)}
                className="sr-only"
              />
              <div
                className={`
                  w-4 h-4 rounded border flex items-center justify-center shrink-0 transition-colors
                  ${
                    projectContext.compliance.includes(option)
                      ? 'bg-accent border-accent'
                      : 'border-surface-300 bg-surface-DEFAULT'
                  }
                `}
              >
                {projectContext.compliance.includes(option) && (
                  <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </div>
              {option}
            </label>
          ))}
        </div>
      </div>
    </div>
  );
}
